import {Cookies, LocalStorage} from '@stripe-internal/stripe-cookies';
import type {HumanSecurityInboundPayload as InboundPayload} from '../../shared/HumanSecurityTypes';
import {INPUT_TAGS} from '../../shared/HumanSecurityTypes';
import type {AppConfig} from '../initialize';
import {initialize} from '../initialize';
import type {SendFn} from '../setupMessages';
import loadHumanSecurityJs from './loadHumanSecurityJs';

export const DEFAULT_APP_ID = 'PXVl48pWoc';

const retrieveHumanSecurityCookies = () => {
  const cookies = new Cookies();
  const localStorage = new LocalStorage();

  let px3 = cookies.get('_px3');
  let pxvid = cookies.get('_pxvid');
  let pxcts = cookies.get('pxcts');
  const x_px_cookies = localStorage.get('x-px-cookies');

  if (px3 === null || pxvid === null || pxcts === null) {
    if (x_px_cookies) {
      const cookiePairs = x_px_cookies
        .split(';')
        .map((pair: string) => pair.trim());
      cookiePairs.forEach((pair) => {
        const equalIndex = pair.indexOf('=');
        if (equalIndex === -1) return; // Skip malformed pairs without '='

        const key = pair.slice(0, equalIndex);
        const value = pair.slice(equalIndex + 1);

        switch (key) {
          case '_px3':
            if (px3 === null) px3 = value;
            break;
          case '_pxvid':
            if (pxvid === null) pxvid = value;
            break;
          case 'pxcts':
            if (pxcts === null) pxcts = value;
            break;
          default:
            break;
        }
      });
    }
  }

  return {
    type: 'response' as const,
    _px3: px3,
    _pxvid: pxvid,
    _pxcts: pxcts,
    _x_px_cookies: x_px_cookies,
  };
};

let scriptLoaded = false;

const loadScript = async (appId: string, respond?: SendFn<'HumanSecurity'>) => {
  try {
    // @ts-expect-error - Property '_pxAppId' does not exist on type 'Window & typeof globalThis'.
    window._pxAppId = appId;

    await loadHumanSecurityJs(appId, respond);

    respond?.({
      type: 'event',
      name: 'human-security-loaded-script',
    });

    scriptLoaded = true;
  } catch (error) {
    respond?.({
      type: 'event',
      name: 'human-security-error-load-error',
      data: {
        error: error instanceof Error ? error.message : 'Unknown error',
      },
    });
  }
};

export const HumanSecurity = async (
  respond: SendFn<'HumanSecurity'>,
  messageData: InboundPayload,
): Promise<void> => {
  const actionTag = messageData.tag;

  switch (actionTag) {
    case INPUT_TAGS.loadScript:
      if (!scriptLoaded) {
        await loadScript(messageData.appId, respond);
      }

      break;

    case INPUT_TAGS.checkLoaded:
      if (!scriptLoaded) {
        await loadScript(messageData.appId ?? DEFAULT_APP_ID);
      }

      respond(retrieveHumanSecurityCookies());
      break;

    default:
      throw new Error(`Unhandled message/request type: ${actionTag}`);
  }
};

const app = (config: AppConfig<'HumanSecurity'>) => {
  const {subscribe} = config;

  subscribe(async (messageData, respond) => {
    await HumanSecurity(respond, messageData);
  });
};

initialize({app, owner: 'checkout'});
